import { ACCESS } from '@/features/access/access';
import { useRoleAccess } from '@/features/access/use-role-access';
import { useEffect, useState } from 'react';
import { ConfirmDialog } from '@/components/organisms/ConfirmDialog';
import { AlertBanner } from '@/components/molecules/AlertBanner';
import { RetryBlock } from '@/features/settings/common';
import { NoticeBannerSkeleton } from './billing-skeletons';
import { getErrorMessage } from '@/lib/errors';
import { useUpgradePromptStore } from '@/lib/stores/upgrade-prompt';
import { useOrgRole } from '@/features/settings/queries';
import { useBillingSummary, usePlansQuery } from '@/features/billing/queries';
import { CAPABILITY_COPY } from '@/features/billing/capability-identifiers';
import {
  managedBillingReason,
  planTierName,
  resolveUnlockingPlan,
  upgradeRowMode,
} from '@/features/billing/plan-helpers';
import { localizedPriceFor, usePricePreview } from '@/features/billing/use-price-preview';
import { BillingPortalButton } from './BillingPortalButton';
import { CheckoutDialog } from './CheckoutDialog';
import { PlanUpgradeDialog } from './PlanUpgradeDialog';
import { usePaddleCheckout } from './use-paddle-checkout';
import { usePlanUpgrade } from './use-plan-upgrade';

/**
 * App-wide upgrade prompt (TRU-543), mounted ONCE in `main.tsx` beside
 * `<ToastProvider />`.
 *
 * Opened by a locked feature's "Upgrade" CTA (carrying the capability the user
 * reached for) and by the `040005 ResourceAccessDenied` backstop in
 * `lib/query-client.ts` (which has no identifier and gets generic copy). A gated
 * click ends in this dialog, never in a red error toast.
 *
 * It returns `null` while closed and only mounts the hook-bearing inner
 * component when open, so the billing queries below never fire app-wide.
 */
export function UpgradePromptDialog() {
  const isOpen = useUpgradePromptStore((s) => s.isOpen);
  if (!isOpen) return null;
  return <UpgradePromptDialogBody />;
}

function UpgradePromptDialogBody() {
  const identifier = useUpgradePromptStore((s) => s.identifier);
  const close = useUpgradePromptStore((s) => s.close);
  const { isOrgOwner } = useOrgRole(); // copy only (owner vs. "ask your owner")
  const { allowed: canManageBilling } = useRoleAccess(ACCESS.billingManage);

  const plansQuery = usePlansQuery({ enabled: canManageBilling });
  const summaryQuery = useBillingSummary();
  const checkout = usePaddleCheckout();
  const flow = usePlanUpgrade({
    isOwner: canManageBilling,
    onNeedsCheckout: (plan) => checkout.start(plan),
  });

  // Once the customer commits, the prompt stands aside for whichever flow dialog
  // took over — and when that one closes, so does the prompt. Without the latch
  // the prompt would re-open behind a cancelled upgrade, which reads as a loop.
  // A checkout that failed with 040701 is the exception: it leaves no dialog on
  // screen, so the prompt comes back to carry the message.
  const [handedOff, setHandedOff] = useState(false);
  const flowIdle = flow.activePlan == null && checkout.checkoutPlan == null;
  useEffect(() => {
    if (handedOff && flowIdle && checkout.errorText == null) close();
  }, [handedOff, flowIdle, checkout.errorText, close]);

  const plans = plansQuery.data ?? [];
  const plan = identifier ? resolveUnlockingPlan(plans, identifier) : null;
  const copy = identifier ? CAPABILITY_COPY[identifier] : null;

  // Same batched Paddle quote the pricing grid uses, so the checkout summary
  // never shows a dollar sign in front of a rupee number. Held back until the
  // subscription read settles — its `paddleCustomerId` is part of the question.
  const pricePreview = usePricePreview(
    plan && !flow.isSubscriptionLoading ? [plan] : [],
    flow.subscription?.paddleCustomerId ?? null,
  );

  const currentPlanRef = flow.subscription?.plan ?? summaryQuery.data?.plan ?? null;
  const currentPlanId = currentPlanRef ? (currentPlanRef.parentId ?? currentPlanRef.id) : null;
  const currentPlan =
    currentPlanId != null ? (plans.find((p) => p.id === currentPlanId) ?? null) : null;

  // Resolved during render, not at click time, so the CTA can say what it will
  // actually do. Reuses the same eligibility rule the pricing grid runs rather
  // than forming a second opinion; `onNeedsCheckout` above still catches a
  // 041002 that slips through on the upgrade path.
  //   - `checkout` — a free org has no live Paddle subscription, so this is a
  //     first-time purchase (the in-place PATCH would 041002).
  //   - `upgrade`  — ACTIVE paid subscriber moving to a strictly pricier plan.
  //   - `portal`   — everything the app deliberately does NOT handle in-app.
  //     This MUST NOT fall through to `checkout.start`: that offers a brand-new
  //     subscription to an org that already holds a live one. It is reachable
  //     here in normal use — an ACTIVE ANNUAL org reaching for a higher tier
  //     bills less per term on a monthly SKU, so `upgradeRowMode` reads the move
  //     as a price decrease and returns `portal` (see TRU-1043).
  const mode = plan
    ? upgradeRowMode({
        target: plan,
        currentPlan,
        subscription: flow.subscription,
        subscriptionErrored: flow.isSubscriptionError,
      })
    : null;

  function handleUpgrade(): void {
    if (!plan || mode === null || mode === 'portal' || mode === 'managed') return;
    setHandedOff(true);
    if (mode === 'upgrade') flow.startUpgrade(plan);
    else checkout.start(plan);
  }

  const title = copy ? `${copy.label} is locked` : 'This feature is locked';
  const isLoading = canManageBilling && (plansQuery.isLoading || flow.isSubscriptionLoading);
  // `portal` has no in-app action, so the confirm button becomes a dismiss and
  // the body carries a real `BillingPortalButton` instead.
  const canUpgrade = canManageBilling && plan != null && mode !== 'portal' && mode !== 'managed';

  return (
    <>
      <ConfirmDialog
        open={!handedOff || checkout.errorText != null}
        onOpenChange={(next) => {
          if (!next) close();
        }}
        title={title}
        description={copy?.preview}
        confirmLabel={canUpgrade ? 'Upgrade' : 'Got it'}
        cancelLabel="Not now"
        confirmDisabled={isLoading}
        onConfirm={canUpgrade ? handleUpgrade : close}
      >
        <div className="flex flex-col gap-3">
          {checkout.errorText && (
            <AlertBanner variant="error">{checkout.errorText}</AlertBanner>
          )}
          {!isOrgOwner ? (
            // Only the org owner can buy. A dead "Upgrade" button for everyone
            // else is worse than no button — say who can do it instead.
            <AlertBanner variant="info">
              Only your workspace owner can change the plan. Ask them to upgrade
              and this unlocks for the whole workspace.
            </AlertBanner>
          ) : isLoading ? (
            // Every resolved branch below is an `AlertBanner`, so the box that
            // shows while the plans read is in flight is the box the copy
            // lands in.
            <NoticeBannerSkeleton />
          ) : plansQuery.isError ? (
            <RetryBlock
              message={getErrorMessage(plansQuery.error, 'Failed to load plans')}
              onRetry={() => void plansQuery.refetch()}
            />
          ) : plan && mode === 'managed' ? (
            <AlertBanner variant="info">
              Available on <span className="font-semibold">{planTierName(plan)}</span> and
              above. {managedBillingReason(flow.subscription)}
            </AlertBanner>
          ) : plan && mode === 'portal' ? (
            // No in-app path exists for this org — never a fresh checkout, which
            // would sell a second subscription to someone who already pays.
            <>
              <AlertBanner variant="info">
                Available on <span className="font-semibold">{planTierName(plan)}</span> and
                above. Change your plan in the billing portal.
              </AlertBanner>
              <BillingPortalButton label="Manage billing" />
            </>
          ) : plan ? (
            <AlertBanner variant="info">
              Available on <span className="font-semibold">{planTierName(plan)}</span> and
              above.
            </AlertBanner>
          ) : (
            <AlertBanner variant="info">
              This isn&rsquo;t part of your current plan. Open Billing to compare
              what each plan includes.
            </AlertBanner>
          )}
        </div>
      </ConfirmDialog>

      <PlanUpgradeDialog flow={flow} />
      <CheckoutDialog
        plan={checkout.checkoutPlan}
        localizedPrice={localizedPriceFor(pricePreview.byPriceId, checkout.checkoutPlan)}
        customerId={flow.subscription?.paddleCustomerId ?? null}
        onClose={checkout.close}
        onProceed={(target, discountId) => void checkout.proceed(target, discountId)}
      />
    </>
  );
}
